/**
 * Stale-chunk recovery.
 *
 * Vite emits every lazy boundary as `assets/<name>-<contenthash>.js`. After a
 * Cloudflare Pages deploy the old hashes are gone, so a tab that loaded the
 * previous `index.html` asks for files that 404 the moment it tries to load
 * anything lazily. The fix is always the same: reload once and pick up the
 * new index.
 *
 * Extracted out of main.js so the matching and the guard can be unit-tested,
 * and so main.js can wire the SAME recovery into all three places an error of
 * this kind can surface:
 *
 *   1. Vue Router's own onError        — navigation-time (mainRouter.js)
 *   2. window unhandledrejection/error — bare dynamic imports
 *   3. app.config.errorHandler         — defineAsyncComponent (see main.js)
 *
 * (3) was missing until 2026-08-14 and is the one that bites hardest: Vue's
 * async-component loader catches the rejection itself, so nothing reaches
 * window, and the component silently renders nothing.
 */

// Shared with mainRouter.js's own handler so a reload from either path
// suppresses the other — one reload, not two.
export const STALE_CHUNK_RELOAD_GUARD = 'edaptio_chunk_reload_at'

// A genuinely broken bundle must not loop the tab. 30s is long enough that a
// second failure means "this build is broken", not "the deploy raced us".
export const STALE_CHUNK_RELOAD_WINDOW_MS = 30_000

// The 30s window on its own only paces a loop, it does not stop one: a build
// that really is missing an asset would reload the tab every 30 seconds for
// as long as the user leaves it open. That was a small risk while the
// patterns only covered assets that 404; it is a real one now they cover the
// commonest failure of all. Three reloads is more than enough for any
// genuine deploy race, so past that the tab gives up and lets the error be
// reported instead — a broken page a user can read beats a page that keeps
// yanking itself out from under them.
export const STALE_CHUNK_RELOAD_COUNT_KEY = 'edaptio_chunk_reload_count'
export const STALE_CHUNK_RELOAD_MAX = 3

// Browser-specific wordings for the same failure, and there are more of them
// than the obvious three. Cloudflare Pages does not 404 a missing asset: with
// no 404.html in the build it answers the SPA fallback, so `/assets/<old
// hash>.css` comes back 200 with `text/html`. A request that is served rather
// than refused produces a whole second family of wordings — the browser got
// bytes, it just cannot use them.
//
// Measured on prod over the week to 2026-09-14, out of 1260 of these errors:
// the three originally listed here covered 212. The three added below covered
// the other 1048, and none of them reloaded — every one left a lazily-loaded
// component rendering nothing, under a spinner that never stops. (HF&VUC
// Nord, 2026-09-14: "vi kan ikke hente afleveringer ned i embedded pdf, den
// står bare og kører" — Kami, being an iframe to another origin, needed no
// chunk and kept working, which is what made it look like a PDF problem.)
const STALE_CHUNK_PATTERNS = [
    // The asset 404s or the network drops it.
    'Failed to fetch dynamically imported module',
    'Importing a module script failed',
    'Expected a JavaScript-or-Wasm module script',
    // The asset resolves to the SPA fallback instead: HTML where JS or CSS
    // was expected. Vite's own preload helper raises the first; Chrome and
    // Firefox raise the other two.
    'Unable to preload CSS for',
    'error loading dynamically imported module',
    'is not a valid JavaScript MIME type',
]

/**
 * Does this rejection/error look like a chunk that no longer exists?
 * Accepts a string, an Error, or anything with a `.message`.
 */
export function looksLikeStaleChunkError(reason) {
    if (! reason) return false
    const msg = (typeof reason === 'string' ? reason : (reason.message || '')) || ''
    return STALE_CHUNK_PATTERNS.some((p) => msg.includes(p))
}

/**
 * Reload once if `reason` is a stale chunk, otherwise do nothing.
 * Returns true when a reload was triggered — used by the tests, and by
 * main.js's error handler to decide whether the error is worth logging.
 */
export function maybeReloadForStaleChunk(reason) {
    if (! looksLikeStaleChunkError(reason)) return false
    try {
        const last = parseInt(sessionStorage.getItem(STALE_CHUNK_RELOAD_GUARD) || '0', 10)
        if (Date.now() - last < STALE_CHUNK_RELOAD_WINDOW_MS) return false

        const count = parseInt(sessionStorage.getItem(STALE_CHUNK_RELOAD_COUNT_KEY) || '0', 10)
        if (count >= STALE_CHUNK_RELOAD_MAX) return false

        sessionStorage.setItem(STALE_CHUNK_RELOAD_GUARD, String(Date.now()))
        sessionStorage.setItem(STALE_CHUNK_RELOAD_COUNT_KEY, String(count + 1))
    } catch { /* sessionStorage unavailable (private mode, blocked) — reload anyway */ }
    window.location.reload()
    return true
}
